import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigationType, useSearchParams } from 'react-router-dom';

/**
 * The filters of a list, or the tab of a page, kept in the query string of
 * the URL (#595). Back, a reload and a copied link keep them.
 *
 * The value on screen is local state: it changes at once, and the URL
 * follows. React Router 7 runs a URL update as a transition, so a field that
 * read the URL lost the keys typed before the transition ended (measured
 * 2026-10-14: "abcdefghij" typed at 10 ms a key gave "ahj"). The hook reads
 * the URL again after a navigation (a link, Back), never after its own
 * writes, which replace the history entry. It reads it in a layout effect,
 * before the browser paints: the scroll restore of Back looks at the page in
 * the next frame (lib/scrollRestore.ts), and the rows of the old filter moved
 * the position away.
 *
 * A write replaces the history entry, so it makes no entry for Back, and it
 * keeps the history state (the list URL that a `BackLink` reads). A value
 * that equals its default leaves the URL, so the default URL stays short.
 * One call can set several values: the Clear button of a filter bar.
 */
export function useUrlParams<K extends string>(defaults: Record<K, string>): [Record<K, string>, (patch: Partial<Record<K, string>>) => void] {
  const [params, setParams] = useSearchParams();
  const { state } = useLocation();
  const navType = useNavigationType();
  const defaultsKey = JSON.stringify(defaults);
  const stableDefaults = useMemo(() => JSON.parse(defaultsKey) as Record<K, string>, [defaultsKey]);
  const keys = useMemo(() => Object.keys(stableDefaults) as K[], [stableDefaults]);
  const fromUrl = useCallback(
    () => Object.fromEntries(keys.map(k => [k, params.get(k) ?? stableDefaults[k]])) as Record<K, string>,
    [keys, params, stableDefaults],
  );
  const [values, setValues] = useState<Record<K, string>>(fromUrl);
  const current = useRef(values);
  current.current = values;

  // A navigation brings a new URL: follow it. Our own writes are REPLACE.
  const urlSignature = keys.map(k => params.get(k) ?? '').join('\u0000');
  useLayoutEffect(() => {
    if (navType !== 'REPLACE') setValues(fromUrl());
  }, [urlSignature, navType, fromUrl]);

  const set = useCallback((patch: Partial<Record<K, string>>) => {
    const next = { ...current.current, ...patch };
    current.current = next;
    setValues(next);
    // The current URL, not the one of the last render: a fast series of
    // writes must not undo each other.
    const p = new URLSearchParams(window.location.search);
    for (const k of keys) {
      const v = next[k];
      if (v === '' || v === stableDefaults[k]) p.delete(k); else p.set(k, v);
    }
    setParams(p, { replace: true, state });
  }, [keys, stableDefaults, setParams, state]);

  return [values, set];
}

/** One value in the URL: a filter, or the value of a tab. See `useUrlParams`. */
export function useUrlParam(key: string, fallback = ''): [string, (value: string) => void] {
  const [values, set] = useUrlParams({ [key]: fallback });
  const setValue = useCallback((value: string) => set({ [key]: value }), [key, set]);
  return [values[key], setValue];
}

/**
 * The tab of a page, in `?tab=`. A value that is not in `allowed` shows the
 * fallback, but the read never writes the URL: a tab that the data of the
 * page allows later (for example, after the first load) still opens.
 */
export function useUrlTab<T extends string>(allowed: readonly T[], fallback: T): [T, (tab: T) => void] {
  const [raw, setRaw] = useUrlParam('tab', fallback);
  const tab = (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
  return [tab, setRaw];
}

/**
 * The history state of a link from a list to its detail page: the URL of
 * the list. `BackLink` on the detail page reads it, and goes back in the
 * history instead of to a new copy of the list.
 */
export function useListFrom(): { from: string } {
  const { pathname, search } = useLocation();
  return { from: pathname + search };
}
